<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

const props = defineProps<{
  data: SectionJobListRecord
  moduleIndex: number
}>()

const { level, isSemantic, invertedStyles } = useSectionRules(
  props.moduleIndex,
  props.data.invertColorTheme,
  !!props.data.title
)

const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null

onMounted(() => {
  if (ctxRef.value) {
    gsap.registerPlugin(ScrollTrigger)

    ctx = gsap.context(() => {
      gsap.defaults({ ease: 'power3' })
      gsap.set('.fx-batch-item', { y: 50, opacity: 0 })

      ScrollTrigger.batch('.fx-batch-item', {
        onEnter: (batch) =>
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            stagger: { each: 0.15 },
            overwrite: true
          })
      })
    }, ctxRef.value as HTMLElement)
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div
    ref="ctxRef"
    class="min-h-screen w-full overflow-hidden py-12 lg:h-auto lg:py-24"
    :class="invertedStyles"
  >
    <div>
      <div
        v-if="data.media"
        class="absolute left-1/2 top-1/3 h-auto w-[640px] max-w-none -translate-x-1/2 lg:bottom-auto lg:top-[-400px] lg:w-[2100px]"
      >
        <Image
          :preload="moduleIndex === 0"
          class="absolute w-full max-w-none object-contain"
          :src="data.media.standardRatio"
          :width="
            data.media.responsiveImage
              ? data.media.responsiveImage?.width
              : (data.media.width ?? 1440)
          "
          :height="
            data.media.responsiveImage
              ? data.media.responsiveImage?.height
              : (data.media.height ?? 1090)
          "
          :alt="data.media?.alt ?? ''"
          sizes="(min-width: 1440px) 1440px, 100vw"
        />
        <svg
          class="absolute left-[28.2%] top-[300px] hidden lg:block"
          width="165"
          height="498"
          viewBox="0 0 165 498"
          fill="none"
        >
          <path
            d="M163.536 498C163.536 315 155.5 107.5 1 0.5"
            stroke="url(#paint0_linear_1248_4151)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_1248_4151"
              x1="88.3147"
              y1="-75.821"
              x2="196.469"
              y2="-69.2374"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#FF69E7" />
              <stop offset="0.345" stop-color="#A168FF" stop-opacity="0.55" />
              <stop offset="0.65" stop-color="#68E4FF" stop-opacity="0.3" />
              <stop offset="1" stop-color="#7DFF68" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div v-if="data.title" class="container relative mb-20 text-left lg:mb-48 lg:text-center">
        <Heading
          v-if="data.title"
          class="mt-14 max-w-5xl uppercase lg:mx-auto lg:mt-8"
          :level="level"
          :size="2"
          :is-semantic="isSemantic"
        >
          <TextReveal :delay="0.5">{{ data.title }}</TextReveal>
        </Heading>
        <div v-if="data.subtitle" class="text mt-4 max-w-4xl lg:mx-auto lg:mt-8 lg:text-xl">
          <TextReveal :delay="0.8">{{ data.subtitle }}</TextReveal>
        </div>
        <CallToAction
          v-if="Array.isArray(data.buttons) && data.buttons.length"
          :data="data.buttons"
          class="mt-4 lg:mx-auto lg:mt-8"
        />
      </div>
    </div>
    <div id="open-positions">
      <div class="container relative">
        <Heading
          v-if="data.boardTitle"
          class="mb-6 max-w-sm uppercase lg:mb-20"
          :level="2"
          :size="3"
          :is-semantic="isSemantic"
        >
          <TextReveal>{{ data.boardTitle }}</TextReveal>
        </Heading>
        <div class="hidden w-full pb-5 text-neutral-500 lg:flex">
          <div class="flex-1"><TextReveal>POSITION</TextReveal></div>
          <div class="flex-1"><TextReveal>Type</TextReveal></div>
          <div class="flex-1"><TextReveal>LOCATION</TextReveal></div>
        </div>
        <JobItem
          v-for="(item, idx) in data.records"
          :key="idx"
          :data="item"
          class="fx-batch-item"
        />
      </div>
    </div>
  </div>
</template>
